"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.RawCashUploadConfigSchema = exports.CashUploadOptionSchema = exports.RawUploadConfigSchema = exports.SupplementalTapePropertiesSchema = exports.RepaymentTapePropertiesSchema = exports.CashBalanceFeedPropertiesSchema = exports.LoanTapePropertiesSchema = exports.LenderVerificationConfigSchema = exports.borrowerEligibilityResultsArtifactSlugSchema = exports.stripArtifactSlugRefMarker = exports.remapArtifactSlugRefs = exports.zArtifactSlugRef = exports.ARTIFACT_SLUG_REF_KEYS_META_KEY = exports.ARTIFACT_SLUG_REF_META_KEY = void 0;
exports.asArtifactSlugRef = asArtifactSlugRef;
const finley_types_1 = require("@finley-technologies/finley-types");
const zod_1 = require("zod");
// ---------------------------------------------------------------------------
// Artifact slug references
// ---------------------------------------------------------------------------
// Metadata key stamped onto a slug-ref field's zod schema. Discovery (clone,
// roundtrip normalizer) reads it off the global registry; it is stripped from
// generated JSON Schema before the admin editor sees it (see
// `stripArtifactSlugRefMarker` + commonTapeNode.getJsonSchema).
exports.ARTIFACT_SLUG_REF_META_KEY = 'slugRef';
// Record-level marker: the KEYS of this `z.record(...)` are themselves artifact
// slug references and must be remapped on clone. Keys can't carry the brand
// directly (a branded key type breaks object-literal construction and string
// indexing), so this rides on the record schema instead.
exports.ARTIFACT_SLUG_REF_KEYS_META_KEY = 'slugRefKeys';
// A branded string marking a config field as a *reference* to another
// artifact's slug (as opposed to a node's own identity slug). Branding is
// compile-time only: the runtime value stays a plain string and the emitted
// JSON Schema stays a plain string. This forces construction sites to
// acknowledge the field while enabling schema-driven discovery/remapping of
// every such reference (replacing per-node hardcoded slug-field lists).
const zArtifactSlugRef = (description) => zod_1.z
    .string()
    .brand()
    .meta({ [exports.ARTIFACT_SLUG_REF_META_KEY]: true, description });
exports.zArtifactSlugRef = zArtifactSlugRef;
function asArtifactSlugRef(slug) {
    return slug;
}
const getZodDef = (schema) => schema._zod?.def;
const isSlugRefSchema = (schema) => zod_1.z.globalRegistry.get(schema)?.[exports.ARTIFACT_SLUG_REF_META_KEY] === true;
const literalValues = (schema) => {
    const def = getZodDef(schema);
    if (def?.type === 'literal')
        return def.values;
    return null;
};
// Selects the single union member that matches `value`: by discriminator when
// the union is discriminated, otherwise by the first member that fully parses.
// Returning one member (never several) avoids double-mapping slug refs.
const pickUnionOption = (options, discriminator, value) => {
    if (discriminator &&
        value !== null &&
        typeof value === 'object' &&
        discriminator in value) {
        const disc = value[discriminator];
        for (const option of options) {
            const discSchema = getZodDef(option)?.shape?.[discriminator];
            const values = discSchema ? literalValues(discSchema) : null;
            if (values && values.includes(disc))
                return option;
        }
    }
    for (const option of options) {
        if (option.safeParse(value).success)
            return option;
    }
    return null;
};
const remapNode = (schema, value, mapSlug) => {
    if (isSlugRefSchema(schema)) {
        return typeof value === 'string' ? mapSlug(value) : value;
    }
    const def = getZodDef(schema);
    if (!def)
        return value;
    switch (def.type) {
        case 'optional':
        case 'nullable':
        case 'default':
        case 'prefault':
        case 'readonly':
        case 'nonoptional':
        case 'catch':
            return remapNode(def.innerType, value, mapSlug);
        case 'object': {
            if (value === null || typeof value !== 'object' || Array.isArray(value)) {
                return value;
            }
            const shape = def.shape;
            const next = {
                ...value,
            };
            for (const [key, childSchema] of Object.entries(shape)) {
                if (key in next) {
                    next[key] = remapNode(childSchema, next[key], mapSlug);
                }
            }
            return next;
        }
        case 'array':
            return Array.isArray(value)
                ? value.map((element) => remapNode(def.element, element, mapSlug))
                : value;
        case 'record': {
            if (value === null || typeof value !== 'object' || Array.isArray(value)) {
                return value;
            }
            // A slug→slug record (e.g. borrowerEligibilityResultsArtifactSlugs) brands
            // its values via `valueType`; its keys can't be brand-typed, so a
            // record-level `slugRefKeys` marker opts them into remapping too.
            const keyIsSlug = isSlugRefSchema(def.keyType) ||
                zod_1.z.globalRegistry.get(schema)?.[exports.ARTIFACT_SLUG_REF_KEYS_META_KEY] === true;
            return Object.fromEntries(Object.entries(value).map(([key, val]) => [
                keyIsSlug ? mapSlug(key) : key,
                remapNode(def.valueType, val, mapSlug),
            ]));
        }
        case 'tuple': {
            if (!Array.isArray(value))
                return value;
            const items = def.items;
            return value.map((element, index) => items[index] ? remapNode(items[index], element, mapSlug) : element);
        }
        case 'intersection':
            return remapNode(def.right, remapNode(def.left, value, mapSlug), mapSlug);
        case 'union': {
            const match = pickUnionOption(def.options, def.discriminator, value);
            return match ? remapNode(match, value, mapSlug) : value;
        }
        case 'lazy':
            return remapNode(def.getter(), value, mapSlug);
        case 'pipe':
            return remapNode(def.in ?? def.out, value, mapSlug);
        default:
            return value;
    }
};
// Walks `schema` alongside `value`, rewriting every branded artifact-slug-ref
// leaf via `mapSlug`. Non-slug fields (and value shapes that don't match the
// schema) pass through untouched. This is the single schema-driven replacement
// for the per-node hardcoded slug-field lists in clone/normalizer code.
const remapArtifactSlugRefs = (schema, value, mapSlug) => remapNode(schema, value, mapSlug);
exports.remapArtifactSlugRefs = remapArtifactSlugRefs;
// Removes the internal slug-ref marker from a generated JSON Schema tree so the
// admin JSON editor never sees the non-standard `slugRef` keyword. Field
// descriptions and every other keyword are left intact.
const stripArtifactSlugRefMarker = (jsonSchema) => {
    const strip = (node) => {
        if (Array.isArray(node)) {
            node.forEach(strip);
            return;
        }
        if (node !== null && typeof node === 'object') {
            delete node[exports.ARTIFACT_SLUG_REF_META_KEY];
            delete node[exports.ARTIFACT_SLUG_REF_KEYS_META_KEY];
            Object.values(node).forEach(strip);
        }
    };
    strip(jsonSchema);
    return jsonSchema;
};
exports.stripArtifactSlugRefMarker = stripArtifactSlugRefMarker;
// Maps loan-product slug (key) → borrower-eligibility-results artifact slug
// (value). Both sides are slug references: the value carries the brand, and the
// keys opt into clone remapping via the record-level `slugRefKeys` marker
// (branding the key type would break object-literal construction / indexing).
exports.borrowerEligibilityResultsArtifactSlugSchema = zod_1.z
    .record(zod_1.z.string(), (0, exports.zArtifactSlugRef)('Borrower eligibility results artifact slug'))
    .describe('Slugs pointing to the borrower eligibility results artifact for each loan product.')
    .meta({ [exports.ARTIFACT_SLUG_REF_KEYS_META_KEY]: true });
exports.LenderVerificationConfigSchema = zod_1.z.object({
    description: zod_1.z.string(),
    receivableResponseTapeArtifactSlug: (0, exports.zArtifactSlugRef)('Slug pointing to the receivable response tape artifact.'),
    borrowingBaseReportArtifactSlug: (0, exports.zArtifactSlugRef)('Slug pointing to the borrowing base report artifact.'),
    borrowerNumbersArtifactSlug: (0, exports.zArtifactSlugRef)('Slug pointing to the artifact that has borrower numbers extracted.'),
    borrowerEligibilityResultsArtifactSlugs: exports.borrowerEligibilityResultsArtifactSlugSchema,
});
// loan tapes must have loanProductSlug and optionally creditFacilitySlug
exports.LoanTapePropertiesSchema = zod_1.z.object({
    creditFacilitySlug: zod_1.z.string().nullable(),
    loanProductSlug: zod_1.z.string(),
});
// cash balance feed must have creditFacilitySlug
exports.CashBalanceFeedPropertiesSchema = zod_1.z.object({
    creditFacilitySlug: zod_1.z.string(),
});
// repayment tapes are not associated with creditFacilitySlug
exports.RepaymentTapePropertiesSchema = zod_1.z.object({
    loanProductSlug: zod_1.z.string(),
});
// supplemental tapes are not associated with creditFacilitySlug or loanProductSlug
exports.SupplementalTapePropertiesSchema = zod_1.z.object({});
/**
 * Configuration for uploading a file to the borrowing base pipeline.
 * This will be used by BBPL monitor.
 */
exports.RawUploadConfigSchema = zod_1.z.object({
    fileExtension: finley_types_1.Util.SupportedFileExtensionSchema.nullable().describe('Type of file supported for upload. For cashBalanceFeedContext this will be null.'),
    baseFilename: zod_1.z
        .string()
        .describe('BaseFile name (example: orgSlug/cfslug/flexloantape) to be used for uploading the file.'),
    externalDisplayName: zod_1.z
        .string()
        .describe('Surfaced on UI by Data Pipeline like "Flex Loan Tape | Fx Supplemental Tape | Cash Balance Feed | etc."'),
    meshName: zod_1.z
        .string()
        .optional()
        .describe('Optional mesh name for the mesh that this file belongs to.'),
});
exports.CashUploadOptionSchema = zod_1.z.enum(['tape', 'fetch', 'form']);
exports.RawCashUploadConfigSchema = exports.RawUploadConfigSchema.extend({
    cashUploadOptions: zod_1.z
        .array(exports.CashUploadOptionSchema)
        .optional()
        .describe('Options to upload cash balances.'),
});
//# sourceMappingURL=tape-builder-base.js.map